/* ============================================================
   Ekaara — sitewide dimensional behaviour
   Runs alongside theme.js, never replaces it. Everything here is
   additive: if this file fails to parse, the store still works.

   3D-reduction pass (Aug 2026): gallery tilt and the continuous
   region-strip scroll drift were removed as unnecessary motion on
   secondary content; card tilt (the one signature hover effect we
   keep) was softened.

   Aug 2026 (2): the hero's diorama stage (.e3d-stage) has always
   carried transform-style: preserve-3d and a perspective on its
   parent, but nothing was ever setting its transform — so the
   "3D hero" never actually tilted. Added the same lightweight
   pointer-driven tilt used on card media, scaled up slightly for
   the full-width diorama.

   Contents
     1. Curtain raiser        (homepage, once per session)
     2. Card tilt             (every product listing on the site)
     3. Mobile nav accordion  (sub-menus in the drawer)
     4. Section motif marks   (quiet watermark behind headings)
     5. 3D rail click/tap fix (all pointer types)
     6. Hero tilt             (pointer-driven parallax on the 3D hero)
   ============================================================ */
(function () {
  'use strict';

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- 1. Curtain raiser ---------------------------------
     The decision to show it was already made synchronously in the
     head. This only drives the choreography and guarantees the
     overlay is torn down afterwards.

     Sequence: the arch draws in and the logo settles (is-set), then
     the whole thing dissolves together (is-open). Simple two-state —
     there's no prop to choreograph any more. */
  function initCurtain() {
    var root = document.documentElement;
    if (!root.hasAttribute('data-curtain')) return;

    var curtain = $('#ekaaraCurtain');
    if (!curtain) { root.removeAttribute('data-curtain'); return; }

    var finished = false;

    function open() {
      if (finished) return;
      finished = true;
      root.setAttribute('data-curtain', 'opening');
      curtain.classList.add('is-open');

      window.setTimeout(function () {
        curtain.classList.add('is-done');
        root.removeAttribute('data-curtain');
        if (curtain.parentNode) curtain.parentNode.removeChild(curtain);
      }, 1000);
    }

    // Let the arch draw in and the logo settle, then dissolve.
    window.requestAnimationFrame(function () { curtain.classList.add('is-set'); });
    var auto = window.setTimeout(open, 2400);

    function early() { window.clearTimeout(auto); open(); }

    $$('[data-curtain-skip]').forEach(function (b) { b.addEventListener('click', early); });
    curtain.addEventListener('click', early);
    document.addEventListener('keydown', early, { once: true });
    window.addEventListener('wheel', early, { once: true, passive: true });
    window.addEventListener('touchstart', early, { once: true, passive: true });

    // Last line of defence: never leave a visitor behind a curtain.
    window.setTimeout(function () {
      if (!finished) open();
      window.setTimeout(function () { root.removeAttribute('data-curtain'); }, 2000);
    }, 6000);
  }

  /* ---------- 2. Card tilt --------------------------------------
     Applies to every .card on the store: collection pages, search
     results, related products, featured grids. */
  function initCardTilt() {
    if (!FINE || REDUCED) return;

    var MAX = 3;      // degrees
    var LIFT = 10;    // px on Z

    $$('.card').forEach(function (card) {
      var media = $('.card__media', card);
      if (!media) return;

      var frame = null;
      var pending = null;

      function paint() {
        frame = null;
        if (!pending) return;
        media.style.transform =
          'translateZ(' + LIFT + 'px) rotateX(' + pending.y + 'deg) rotateY(' + pending.x + 'deg)';
      }

      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        if (!r.width || !r.height) return;
        pending = {
          x: ((e.clientX - r.left) / r.width - 0.5) * (MAX * 2),
          y: ((e.clientY - r.top) / r.height - 0.5) * -(MAX * 2)
        };
        if (!frame) frame = window.requestAnimationFrame(paint);
      });

      card.addEventListener('pointerleave', function () {
        pending = null;
        if (frame) { window.cancelAnimationFrame(frame); frame = null; }
        media.style.transform = '';
      });
    });
  }

  /* ---------- 3. Mobile nav accordion --------------------------- */
  function initDrawerAccordion() {
    $$('[data-mnav-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.closest('.mnav__group');
        if (!group) return;
        var open = group.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- 4. Section motif marks ----------------------------
     Removed (Aug 2026): this used to drop a large, faint regional
     motif behind each section heading. Left as a stub — intentionally
     disabled — rather than deleted, since the boot loop below still
     calls it. No .rmotif-mark element is created or inserted. */
  function initSectionMarks() {
    return;
  }

  /* ---------- 5. 3D rail click/tap fix ---------------------------
     Two separate bugs stacked on this rail, and both have to be
     fixed for a tap to actually navigate.

     First: the coverflow drag surface (.e3d-rail-track) calls
     stage.setPointerCapture() on every pointerdown, mouse included.
     Per the Pointer Events spec, once an element has pointer
     capture, compatibility mouse events — including the synthesized
     "click" — are retargeted to the CAPTURING element instead of
     whatever is actually under the pointer, so a native click on
     the product <a class="e3d-tag"> never fires at all.

     Second, and the reason a naive elementFromPoint() fix still
     failed: .e3d-rail-track is a flat, untransformed div the exact
     size of the whole rail, sitting inside a perspective/preserve-3d
     stack alongside the individually matrix3d-transformed card
     anchors. Chrome's hit-testing in that combination resolves
     elementFromPoint() to the flat track every time, never
     descending into the transformed anchors — confirmed directly:
     elementFromPoint() at the exact centre of a card's own
     getBoundingClientRect() still returns .e3d-rail-track, even
     though the card sits at z-index:1000 above it. The anchors'
     getBoundingClientRect() is correct (it matches what's on
     screen); only the browser's point-based hit test is wrong here.

     Fix: never ask the browser what's under the pointer. Track the
     tap ourselves (moved <8px, lifted within 600ms = a tap, not a
     drag), then find the card by geometry — the .e3d-tag whose own
     bounding rect contains the point, highest z-index wins on
     overlap. A modifier key (cmd/ctrl/shift) or the middle mouse
     button opens the product in a new tab, matching a native link. */
  function initRailTap() {
    $$('.e3d-rail-stage').forEach(function (stage) {
      var sx = 0, sy = 0, st = 0, armed = false;

      function findTag(x, y) {
        var best = null, bestZ = -Infinity;
        $$('.e3d-tag', stage).forEach(function (t) {
          var r = t.getBoundingClientRect();
          if (x < r.left || x > r.right || y < r.top || y > r.bottom) return;
          var z = parseInt(window.getComputedStyle(t).zIndex, 10);
          if (isNaN(z)) z = 0;
          if (!best || z > bestZ) { best = t; bestZ = z; }
        });
        return best;
      }

      stage.addEventListener('pointerdown', function (e) {
        sx = e.clientX; sy = e.clientY; st = Date.now(); armed = true;
      }, true);

      stage.addEventListener('pointerup', function (e) {
        if (!armed) return;
        armed = false;
        if (Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8) return;
        if (Date.now() - st > 600) return;

        var link = findTag(e.clientX, e.clientY);
        if (!link || !link.href) return;

        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
          window.open(link.href, '_blank', 'noopener');
        } else {
          window.location.href = link.href;
        }
      }, true);

      stage.addEventListener('pointercancel', function () { armed = false; }, true);
    });
  }

  /* ---------- 6. Hero tilt ---------------------------------------
     The 3D hero's .e3d-stage already has transform-style: preserve-3d
     and its parent already has perspective set in CSS, purely so the
     translateZ()'d layers (cloth, side rails, copy) read as a
     diorama. That's a static illusion on its own — this is what
     actually moves it: tracks the pointer across the whole hero and
     applies a small rotateX/rotateY to the stage, same easing model
     as the card tilt above but subtler, since it's swinging the
     entire hero rather than one card. Skipped on touch devices and
     when the visitor has asked for reduced motion. */
  function initHeroTilt() {
    if (!FINE || REDUCED) return;

    var MAX = 5; // degrees — full-width diorama, so kept gentle

    $$('.e3d-hero').forEach(function (hero) {
      var stage = $('.e3d-stage', hero);
      if (!stage) return;

      var frame = null;
      var pending = null;

      function paint() {
        frame = null;
        if (!pending) return;
        stage.style.transform = 'rotateX(' + pending.y + 'deg) rotateY(' + pending.x + 'deg)';
      }

      hero.addEventListener('pointermove', function (e) {
        var r = hero.getBoundingClientRect();
        if (!r.width || !r.height) return;
        pending = {
          x: ((e.clientX - r.left) / r.width - 0.5) * (MAX * 2),
          y: ((e.clientY - r.top) / r.height - 0.5) * -(MAX * 2)
        };
        if (!frame) frame = window.requestAnimationFrame(paint);
      });

      hero.addEventListener('pointerleave', function () {
        pending = null;
        if (frame) { window.cancelAnimationFrame(frame); frame = null; }
        stage.style.transform = '';
      });
    });
  }

  /* ---------- Boot ---------- */
  function safe(fn) {
    try { fn(); } catch (e) {
      if (window.console && console.warn) console.warn('[ekaara-3d]', fn.name, e);
    }
  }

  function boot() {
    safe(initCurtain);
    safe(initCardTilt);
    safe(initDrawerAccordion);
    safe(initSectionMarks);
    safe(initRailTap);
    safe(initHeroTilt);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
